// @ts-nocheck
/**
 * Finance chat context: talk to the finance agent (AgentOS).
 *
 * Finance AI is briefing-first: use "Get guidance" (sub-page banner) or
 * "Talk to AI" (dashboard task) for guided flows; the chat input is for
 * follow-up when you need to go more granular.
 *
 * State is held in a React context so it persists across finance sub-page
 * navigation. Provider mounts in App (with AIChatProvider); opening the assistant
 * uses AIChatContext source `finance` so the shared Ophelos panel shows FinanceChatPanel.
 */

import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { BankStatementLine } from '@/apps/finances/types/bankStatements';
import { useAIChat } from '@/contexts/AIChatContext';
import { useHub } from '@/contexts/HubContext';
import { getAgentosUrl } from '@/lib/agentos-url';
import { getAccessToken } from '@/lib/auth';

/** Discriminated union describing where the AI panel was opened from. */
export type ChatOrigin =
  | { type: 'dashboard'; lifecycleId?: string }
  | { type: 'bank_line'; line: BankStatementLine }
  | { type: 'payout'; payoutId: string; payoutStatus: string }
  | { type: 'psp_matching'; lineId?: string }
  | { type: 'generic' };

const FINANCE_AGENT_ID = 'finance-chat-agent';
const REQUEST_TIMEOUT_MS = 90_000;

export interface FinanceChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

function operatorLocale(operator: { code?: string } | null): string {
  const code = operator?.code?.toUpperCase() ?? '';
  if (code.endsWith('_PT') || code.includes('PORTUGAL')) return 'pt';
  if (code.endsWith('_ES') || code.includes('SPAIN')) return 'es';
  if (code.endsWith('_FR') || code.includes('FRANCE')) return 'fr';
  return 'en';
}

interface FinanceAgentChatValue {
  messages: FinanceChatMessage[];
  isLoading: boolean;
  error: string | null;
  /** Optional sessionStateOverride: lifecycle_id, status_counts, briefing_items — used when opening with a message so the first request gets briefing context. */
  sendMessage: (
    userMessage: string,
    sessionStateOverride?: {
      lifecycle_id?: string;
      status_counts?: Record<string, number>;
      briefing_items?: BriefingItemForFlow[];
      psp_line_id?: string;
    }
  ) => Promise<void>;
  /** Open the assistant panel and send this message immediately (guided flow). Optionally pass lifecycleId, items, statusCounts, and origin for context-aware cards. */
  openWithMessage: (
    message: string,
    lifecycleId?: string,
    items?: BriefingItemForFlow[],
    statusCounts?: Record<string, number>,
    origin?: ChatOrigin
  ) => void;
  clearError: () => void;
  canChat: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  /** True when panel was opened via openWithMessage; use for default panel width. Cleared on close or when user sends from input. */
  openAtMaxWidth: boolean;
  /** When set, show LifecycleFlow at top of panel (e.g. after "Talk to AI" from briefing). Cleared when user sends from input. */
  lifecycleIdForFlow: string | null;
  /** Items for per-item flow (click transaction to see its flowchart). Cleared with clearLifecycleForFlow. */
  briefingItemsForFlow: BriefingItemForFlow[];
  /** Selected item id for showing that item's flow + Proceed link. */
  selectedBriefingItemId: string | null;
  setSelectedBriefingItemId: (id: string | null) => void;
  /** Status counts for the current lifecycle flow (e.g. { pending: 2, classified: 1 }). */
  statusCountsForFlow: Record<string, number>;
  clearLifecycleForFlow: () => void;
  chatInput: string;
  setChatInput: (input: string) => void;
  /** User dismissed the match suggestion card; persists across panel close/reopen. Reset when a new assistant reply arrives. */
  dismissedMatchCard: boolean;
  setDismissedMatchCard: (dismissed: boolean) => void;
  /** User dismissed the "Find matches for a bank line" picker card; same persistence rules. */
  dismissedLinePickerCard: boolean;
  setDismissedLinePickerCard: (dismissed: boolean) => void;
  /** Where the AI panel was opened from — used for context-aware card selection. */
  chatOrigin: ChatOrigin | null;
}

export interface BriefingItemForFlow {
  id: string;
  label: string;
  current_step: string;
  link: string;
}

const FinanceAgentChatContext = createContext<FinanceAgentChatValue | null>(null);

export function FinanceAgentChatProvider({ children }: { children: ReactNode }) {
  const { selectedOperator } = useHub();
  const { isOpen: isAIChatOpen, resolvedOpenSource, open: openAIChat, close: closeAIChat } = useAIChat();
  const operatorId = selectedOperator?.id ?? null;
  const locale = operatorLocale(selectedOperator);

  const [messages, setMessages] = useState<FinanceChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openAtMaxWidth, setOpenAtMaxWidth] = useState(false);
  const [lifecycleIdForFlow, setLifecycleIdForFlow] = useState<string | null>(null);
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (!open) {
        closeAIChat();
        setOpenAtMaxWidth(false);
        setLifecycleIdForFlow(null);
        setBriefingItemsForFlow([]);
        setSelectedBriefingItemId(null);
        setStatusCountsForFlow({});
        setChatOrigin(null);
        return;
      }
      openAIChat({ source: 'finance' });
    },
    [openAIChat, closeAIChat]
  );
  const [briefingItemsForFlow, setBriefingItemsForFlow] = useState<BriefingItemForFlow[]>([]);
  const [selectedBriefingItemId, setSelectedBriefingItemId] = useState<string | null>(null);
  const [statusCountsForFlow, setStatusCountsForFlow] = useState<Record<string, number>>({});
  const [chatInput, setChatInput] = useState('');
  const [dismissedMatchCard, setDismissedMatchCard] = useState(false);
  const [dismissedLinePickerCard, setDismissedLinePickerCard] = useState(false);
  const [chatOrigin, setChatOrigin] = useState<ChatOrigin | null>(null);
  const sessionIdRef = useRef(`finance-chat-${operatorId ?? 'none'}-${Date.now()}`);
  const prevMessageCountRef = useRef(messages.length);

  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      const lastMessage = messages[messages.length - 1];
      const isNewReplyFromAssistant = lastMessage?.role === 'assistant';
      // Reset match suggestion card on each new assistant reply (so new suggestions show).
      setDismissedMatchCard(!isNewReplyFromAssistant);
      // Line picker / find-match card: once the user has interacted (dismissedLinePickerCard=true),
      // keep it dismissed for the rest of the conversation. Only reset on new conversation (operatorId change / openWithMessage).
      prevMessageCountRef.current = messages.length;
    }
  }, [messages.length, messages]);

  useEffect(() => {
    sessionIdRef.current = `finance-chat-${operatorId ?? 'none'}-${Date.now()}`;
    setMessages([]);
    setError(null);
    setLifecycleIdForFlow(null);
    setBriefingItemsForFlow([]);
    setSelectedBriefingItemId(null);
    setStatusCountsForFlow({});
    setDismissedMatchCard(false);
    setDismissedLinePickerCard(false);
    setChatOrigin(null);
    prevMessageCountRef.current = 0;
  }, [operatorId]);

  const sendMessage = useCallback(
    async (
      userMessage: string,
      sessionStateOverride?: {
        lifecycle_id?: string;
        status_counts?: Record<string, number>;
        briefing_items?: BriefingItemForFlow[];
      }
    ) => {
      const text = userMessage.trim();
      if (!text || isLoading) return;
      if (!operatorId) {
        setError('Select an operator to chat.');
        return;
      }

      setError(null);
      setMessages((prev) => [...prev, { role: 'user', content: text }]);
      setIsLoading(true);

      const lifecycleId = sessionStateOverride?.lifecycle_id !== undefined ? sessionStateOverride.lifecycle_id : lifecycleIdForFlow;
      const statusCountsOverride = sessionStateOverride?.status_counts ?? (sessionStateOverride ? undefined : statusCountsForFlow);
      const briefingItemsOverride =
        sessionStateOverride?.briefing_items ??
        (sessionStateOverride ? undefined : briefingItemsForFlow.length > 0 ? briefingItemsForFlow : undefined);
      const pspLineId = sessionStateOverride?.psp_line_id;

      const formData = new URLSearchParams();
      formData.append('message', text);
      formData.append('stream', 'false');
      formData.append('session_id', sessionIdRef.current);
      formData.append(
        'session_state',
        JSON.stringify({
          operator_id: operatorId,
          locale: locale || 'en',
          lifecycle_id: lifecycleId ?? undefined,
          ...(statusCountsOverride && Object.keys(statusCountsOverride).length > 0 ? { status_counts: statusCountsOverride } : {}),
          ...(briefingItemsOverride && briefingItemsOverride.length > 0 ? { briefing_items: briefingItemsOverride } : {}),
          ...(pspLineId ? { psp_line_id: pspLineId } : {}),
        })
      );

      const accessToken = await getAccessToken();
      const headers: HeadersInit = { 'Content-Type': 'application/x-www-form-urlencoded' };
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      try {
        const res = await fetch(`${getAgentosUrl()}/agents/${FINANCE_AGENT_ID}/runs`, {
          method: 'POST',
          headers,
          body: formData,
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error(`Agent error: ${res.status}`);

        const data = (await res.json()) as { content?: string };
        const content = data.content?.trim() || 'I couldn\u2019t generate a response. Please try again.';
        setMessages((prev) => [...prev, { role: 'assistant', content }]);
      } catch (err) {
        clearTimeout(timeoutId);
        if (err instanceof Error) {
          if (err.name === 'AbortError') setError('Request timed out. Try a shorter question or check AgentOS logs.');
          else setError(err.message);
        } else setError('Something went wrong. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [operatorId, locale, isLoading, lifecycleIdForFlow, statusCountsForFlow, briefingItemsForFlow]
  );

  const openWithMessage = useCallback(
    (message: string, lifecycleId?: string, items?: BriefingItemForFlow[], statusCounts?: Record<string, number>, origin?: ChatOrigin) => {
      const text = message.trim();
      if (!text) return;
      // New conversation: clear history and session so payouts/bank statements don't mix
      setMessages([]);
      setError(null);
      sessionIdRef.current = `finance-chat-${operatorId ?? 'none'}-${Date.now()}`;
      setOpenAtMaxWidth(true);
      openAIChat({ source: 'finance' });
      if (lifecycleId) setLifecycleIdForFlow(lifecycleId);
      setBriefingItemsForFlow(items ?? []);
      setSelectedBriefingItemId(null);
      setStatusCountsForFlow(statusCounts ?? {});
      setChatOrigin(origin ?? null);
      void sendMessage(text, {
        ...(lifecycleId ? { lifecycle_id: lifecycleId } : {}),
        ...(statusCounts && Object.keys(statusCounts).length > 0 ? { status_counts: statusCounts } : {}),
        ...(items && items.length > 0 ? { briefing_items: items } : {}),
        ...(origin?.type === 'psp_matching' && origin.lineId ? { psp_line_id: origin.lineId } : {}),
      });
    },
    [sendMessage, operatorId, openAIChat]
  );

  const financePanelOpen = isAIChatOpen && resolvedOpenSource === 'finance';

  const value: FinanceAgentChatValue = {
    messages,
    isLoading,
    error,
    sendMessage,
    openWithMessage,
    clearError: () => setError(null),
    canChat: !!operatorId,
    isOpen: financePanelOpen,
    setIsOpen,
    openAtMaxWidth,
    lifecycleIdForFlow,
    briefingItemsForFlow,
    selectedBriefingItemId,
    setSelectedBriefingItemId,
    statusCountsForFlow,
    clearLifecycleForFlow: () => {
      setLifecycleIdForFlow(null);
      setBriefingItemsForFlow([]);
      setSelectedBriefingItemId(null);
      setStatusCountsForFlow({});
      // Do not set openAtMaxWidth(false) here — only when panel is closed (setIsOpen)
    },
    chatInput,
    setChatInput,
    dismissedMatchCard,
    setDismissedMatchCard,
    dismissedLinePickerCard,
    setDismissedLinePickerCard,
    chatOrigin,
  };

  return <FinanceAgentChatContext.Provider value={value}>{children}</FinanceAgentChatContext.Provider>;
}

export function useFinanceAgentChat(): FinanceAgentChatValue {
  const ctx = useContext(FinanceAgentChatContext);
  if (!ctx) throw new Error('useFinanceAgentChat must be used within FinanceAgentChatProvider');
  return ctx;
}
